import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowRight, ArrowLeft, BookOpen, GraduationCap, ChevronRight } from "lucide-react";
import { ListenButton } from "./ListenButton";
import { ReadingTime } from "./ReadingTime";
import { ArticleAudioPlayer } from "./ArticleAudioPlayer";
import { FOUNDATIONS, foundationIndex } from "../config/foundations";
import { usePreviewMode } from "../hooks/usePreviewMode";
import { previewHeaders } from "../lib/previewAuth";
import { FeaturedPerspectiveCard } from "./perspectives/FeaturedPerspectiveCard";

// A dedicated, editorial chapter for each of the Six Foundations.
export default function Foundation() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const idx = foundationIndex(slug);
  const [related, setRelated] = useState(null);
  const [featuredPerspective, setFeaturedPerspective] = useState(null);
  const previewMode = usePreviewMode();

  const foundations = t("home.foundations", { returnObjects: true }) || [];
  const pages = t("foundationPages.items", { returnObjects: true }) || [];
  const node = idx >= 0 ? foundations[idx] : null;
  const page = idx >= 0 ? pages[idx] : null;
  const meta = idx >= 0 ? FOUNDATIONS[idx] : null;

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!meta) return;
    let active = true;
    (async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const res = await axios.get(`${backendUrl}/api/articles?lang=${lang}`);
        const found = (res.data || []).find((a) => a.id === meta.article);
        if (active) setRelated(found || null);
      } catch (e) { /* silent */ }
    })();
    return () => { active = false; };
  }, [meta, lang]);

  // Featured Perspective — currently anchored to the Technology foundation.
  // Fetched from the categoryKey field on each perspective; other foundations
  // will light up automatically as future essays are published under them.
  useEffect(() => {
    if (!meta) return;
    let active = true;
    (async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const url = `${backendUrl}/api/perspectives?lang=${lang}${previewMode ? "&include_drafts=true" : ""}`;
        const res = await axios.get(url, { headers: previewMode ? previewHeaders() : {} });
        const match = (res.data || []).find((p) => p.categoryKey === meta.slug) || null;
        if (active) setFeaturedPerspective(match);
      } catch (e) { /* silent */ }
    })();
    return () => { active = false; };
  }, [meta, lang, previewMode]);

  if (idx < 0 || !node || !page) return <Navigate to="/world" replace />;

  const prev = FOUNDATIONS[(idx + FOUNDATIONS.length - 1) % FOUNDATIONS.length];
  const next = FOUNDATIONS[(idx + 1) % FOUNDATIONS.length];
  const prevNode = foundations[(idx + foundations.length - 1) % foundations.length];
  const nextNode = foundations[(idx + 1) % foundations.length];
  const narration = [node.title, page.intro, page.essayTitle, ...(page.essayBody || [])].filter(Boolean).join(" ");

  return (
    <div className="pb-24" data-testid="foundation-page">
      {/* Breadcrumb — always know where you are */}
      <nav className="max-w-7xl mx-auto px-6 md:px-12 pt-6" aria-label="Breadcrumb" data-testid="foundation-breadcrumb">
        <ol className="flex items-center gap-2 text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom">
          <li><Link to="/" className="hover:text-[var(--accent-bronze)] transition-colors">{t("nav.home")}</Link></li>
          <ChevronRight className="w-3 h-3 opacity-50" />
          <li><Link to="/world" className="hover:text-[var(--accent-bronze)] transition-colors">{t("foundationPages.allFoundations")}</Link></li>
          <ChevronRight className="w-3 h-3 opacity-50" />
          <li className="text-primary-custom">{node.title}</li>
        </ol>
      </nav>

      {/* Cinematic hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8">
        <div className="relative overflow-hidden border border-subtle-custom" data-testid="foundation-hero">
          <div className="aspect-[16/10] sm:aspect-[21/9]">
            <img src={meta.image} alt={node.title} className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 p-8 md:p-14 space-y-3">
            <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-white/70 font-semibold block">
              {t("foundationPages.eyebrow")} · {String(idx + 1).padStart(2, "0")}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light text-white tracking-tight leading-[1.05] max-w-3xl">{node.title}</h1>
          </div>
        </div>
      </section>

      {/* Intro + essay */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-16 space-y-10">
        <p className="font-serif text-xl sm:text-2xl text-primary-custom leading-relaxed font-light">{page.intro}</p>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="hidden md:block w-10 h-px bg-[var(--accent-bronze)]" />
          <ArticleAudioPlayer
            text={narration}
            pageType="foundation"
            pageSlug={page.slug || page.id || page.categoryKey}
            testId="foundation-listen"
          />
          <ReadingTime text={[page.intro, page.essayTitle, ...(page.essayBody || [])]} testId="foundation-reading-time" pageType="foundation" pageSlug={page.slug || page.id || page.categoryKey} />
        </div>
        <article className="space-y-6" data-testid="foundation-essay">
          <h2 className="text-2xl sm:text-3xl font-serif text-primary-custom tracking-tight">{page.essayTitle}</h2>
          {(page.essayBody || []).map((para, i) => (
            <p key={i} className="font-serif text-base sm:text-lg text-secondary-custom leading-relaxed font-light">{para}</p>
          ))}
        </article>

        {/* Reflection */}
        <div className="border-l-2 border-[var(--accent-bronze)] pl-6 py-2 my-4" data-testid="foundation-reflection">
          <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block mb-2">{t("foundationPages.reflectionLabel")}</span>
          <p className="font-serif text-lg sm:text-xl italic text-primary-custom leading-relaxed">{page.reflection}</p>
        </div>
      </section>

      {/* Evidence & further reading — rendered only when the foundation has
          citations. Visually secondary to the essay: small caps eyebrow, thin
          bronze rule, muted serif details. This is not a bibliography — it is
          a short editorial note directing the interested reader to primary sources. */}
      {(page.evidenceItems || []).length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 pt-10" data-testid="foundation-evidence">
          <div className="border-t border-subtle-custom pt-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[var(--accent-bronze)]/60" />
              <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom font-semibold">
                {t("foundationPages.evidenceLabel")}
              </span>
            </div>
            <p className="font-serif text-sm italic text-secondary-custom leading-relaxed mb-6 max-w-prose">
              {t("foundationPages.evidenceNote")}
            </p>
            <ol className="space-y-4 list-decimal pl-5 marker:text-secondary-custom/50 marker:text-xs">
              {page.evidenceItems.map((it, i) => (
                <li key={i} className="font-serif text-sm text-secondary-custom leading-relaxed">
                  <span>{it.citation} </span>
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`foundation-evidence-link-${i}`}
                    className="inline text-primary-custom underline decoration-[var(--accent-bronze)] decoration-1 underline-offset-4 hover:text-[var(--accent-bronze)] transition-colors break-words"
                  >
                    {it.url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* Suggested reading + Future courses */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-10 grid grid-cols-1 sm:grid-cols-2 gap-10">
        <div className="space-y-4" data-testid="foundation-readings">
          <div className="flex items-center gap-2.5 text-[var(--accent-bronze)]">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold">{t("foundationPages.readingsLabel")}</span>
          </div>
          <ul className="space-y-2.5">
            {(page.readings || []).map((r, i) => (
              <li key={i} className="font-serif text-sm text-secondary-custom leading-relaxed border-b border-subtle-custom pb-2.5">{r}</li>
            ))}
          </ul>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 text-[var(--accent-bronze)]">
            <GraduationCap className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold">{t("foundationPages.coursesLabel")}</span>
          </div>
          <p className="font-serif text-sm italic text-secondary-custom leading-relaxed">{page.courses}</p>
        </div>
      </section>

      {/* Featured Perspective — long-form editorial essay anchored to this foundation.
          Renders only when a perspective is available (published or preview-visible). */}
      {featuredPerspective && (
        <FeaturedPerspectiveCard
          testId="foundation-featured-perspective"
          eyebrow={t("perspectives.featured")}
          categoryLabel={featuredPerspective.category}
          title={featuredPerspective.title}
          subtitle={featuredPerspective.subtitle}
          href={`/perspectives/${featuredPerspective.slug}`}
          linkLabel={t("perspectives.readPerspective")}
          hero={featuredPerspective.hero?.image ? { image: featuredPerspective.hero.image, alt: featuredPerspective.hero.alt } : null}
          heroPlaceholder={{
            label: t("perspectives.draftHeroLabel"),
            caption: t("perspectives.draftHeroCaption"),
          }}
          isDraft={featuredPerspective.status === "draft"}
        />
      )}

      {/* Related journal essay */}
      {related && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
          <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-[var(--accent-bronze)] font-semibold block mb-6">{t("foundationPages.relatedJournalLabel")}</span>
          <Link to={`/journal?article=${related.id}`} data-testid="foundation-related-article" className="group grid grid-cols-1 md:grid-cols-2 border border-subtle-custom hover:border-[var(--accent-bronze)] transition-all duration-500 overflow-hidden">
            <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
              <img src={related.image} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center space-y-4">
              <div className="flex items-center gap-3 text-[10px] font-sans tracking-wide text-secondary-custom uppercase"><span>{related.author}</span></div>
              <h3 className="text-2xl font-serif text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{related.title}</h3>
              <p className="font-sans text-sm text-secondary-custom leading-relaxed">{related.summary}</p>
              <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-primary-custom font-semibold pt-2">{t("home.readEssay")}<ArrowRight className="w-3.5 h-3.5" /></span>
            </div>
          </Link>
        </section>
      )}

      {/* Chapter navigation — guided flow, but free to move */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
        <div className="border-t border-subtle-custom pt-8 flex items-stretch justify-between gap-4">
          <Link to={`/foundations/${prev.slug}`} data-testid="foundation-prev" className="group flex-1 flex flex-col gap-1.5">
            <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom"><ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-1" />{t("nav.back")}</span>
            <span className="font-serif text-base text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{prevNode.title}</span>
          </Link>
          <Link to="/world" data-testid="foundation-all-link" className="hidden sm:flex items-center px-6 text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors border-x border-subtle-custom">
            {t("foundationPages.allFoundations")}
          </Link>
          <Link to={`/foundations/${next.slug}`} data-testid="foundation-next" className="group flex-1 flex flex-col gap-1.5 text-right items-end">
            <span className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom">{t("foundationPages.exploreLabel")}<ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" /></span>
            <span className="font-serif text-base text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors">{nextNode.title}</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
